import React, { forwardRef, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';

export type ValidationState = 'valid' | 'invalid' | 'none';

interface InputFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    labelText?: string;
    fieldRequired?: boolean;
    fieldPlaceholder?: string;
    validationState?: ValidationState;
    error?: string | null;
    rows?: number;
}

const autoGrow = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const field = e.target as HTMLTextAreaElement;
    
    // Reset height to auto to get the correct scrollHeight
    field.style.height = 'auto';
    
    // Set to scrollHeight to grow/shrink accordingly
    field.style.height = `${field.scrollHeight}px`;
    
    // Ensure minimum height based on rows prop
    const lineHeight = parseInt(getComputedStyle(field).lineHeight);
    const minHeight = lineHeight * (field.rows || 1);
    if (parseInt(field.style.height) < minHeight) {
        field.style.height = `${minHeight}px`;
    }
};

const InputTextArea = forwardRef<HTMLTextAreaElement, InputFieldProps>(
    ({ labelText, fieldRequired, fieldPlaceholder, validationState = 'none', error = null, className, rows = 1, ...props }, ref) => {
        const [isFocused, setIsFocused] = useState(false);
        const [inputLength, setInputLength] = useState(0);

        useEffect(() => {
            setInputLength(props.value ? String(props.value).length : 0);
        }, [props.value]);

        const handleInputChange = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            const field = e.target as HTMLTextAreaElement;
            autoGrow(e);
            setInputLength(field.value.length);
        }

        const handleFocus = () => {
            setIsFocused(true);
        };

        const handleBlur = () => {
            setIsFocused(false);
        };

        return (
            <div className="relative">
                <label className="relative block">
                    <div className="label-container cursor-text">
                        <span className="input-boundary-box relative flex items-center space-y-1">
                            <span className={`input-container relative flex-1 ${error ? 'error' : ''}`}>
                                <textarea
                                    ref={ref}
                                    placeholder={isFocused ? fieldPlaceholder : ''}
                                    rows={rows}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onInput={handleInputChange}
                                    className={`
                                        eoe-input min-h-[60px] w-full overflow-y-hidden pl-3 pr-8 pt-6 pb-1
                                        bg-gray-200 dark:bg-eoe-dark-input
                                        rounded-2xl
                                        text-gray-800 dark:text-eoe-dark-text
                                        transition-colors
                                        resize-none cursor-text
                                        ${error ? 'text-warning-red dark:text-dark-warning-red' : ''}
                                        ${className || ''}
                                    `}
                                    {...props}
                                />
                                <span 
                                    className={`
                                        absolute left-3 
                                        ${isFocused ? 'top-2 text-xs' : inputLength === 0 ? 'top-1/2 -translate-y-1/2' : 'top-2 text-xs'}
                                        transition-all
                                        ${error ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}
                                    `}
                                >
                                    {labelText}
                                    {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                                </span>
                            </span>
                            {props.maxLength && (
                                <span className="absolute right-3 top-0.5 text-[10px] text-eoe-text-meta dark:text-eoe-dark-text-meta flex-shrink-0 place-items-end items-end text-right"><span className={`text-[10px] ${error || validationState === 'invalid' ? 'text-warning-red dark:text-dark-warning-red' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta'}`}>{inputLength}</span> / {props.maxLength}
                                </span>
                            )}
                            
                            {validationState !== 'none' && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                                    {validationState === 'valid' && !error ? (
                                        <Check className="h-5 w-5 text-green-600" />
                                    ) : validationState === 'invalid' ? (
                                        <X className="h-5 w-5 text-warning-red dark:text-dark-warning-red" />
                                    ) : null}
                                </span>
                            )}
                        </span>
                    </div>
                </label>
                {error && (
                    <p className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">{error}</p>
                )}
            </div>
        );
    }
);

InputTextArea.displayName = 'InputTextArea';

export default InputTextArea;